import { useState, useCallback } from 'react';
import type { Manager, Repositories } from 'coco-cashu-core';
import { useToast } from '@/hooks/useToast';
import { probeMintReachability } from '@/lib/mintReachability';

export interface CashuMint {
  url: string;
  name?: string;
}

// Mint utility functions
export const getCleanMintLabel = (mint: { name?: string; url: string }): string => {
  if (mint.name && mint.name.trim().length > 0) {
    return mint.name.replace(/^https?:\/\//, '');
  }
  try {
    return new URL(mint.url).host;
  } catch {
    return mint.url.replace(/^https?:\/\//, '');
  }
};

export const isUnknownMintError = (err: unknown): boolean => {
  const errorMessage = err instanceof Error ? err.message : String(err);
  const errorName = err instanceof Error ? err.constructor.name : '';
  return errorName === 'UnknownMintError' || 
         errorMessage.includes('UnknownMintError') || 
         (errorMessage.includes('Mint') && errorMessage.includes('is not known'));
};

export const extractMintUrlFromError = (err: unknown): string | null => {
  const errorMessage = err instanceof Error ? err.message : String(err);
  const mintUrlMatch = errorMessage.match(/Mint (https?:\/\/[^\s]+)/);
  return mintUrlMatch?.[1] || null;
};

interface UseMintManagerProps {
  coco: Manager | null;
  mints: CashuMint[];
  repositories: Repositories | null;
  refreshMints: () => Promise<void>;
}

export function useMintManager({
  coco,
  mints,
  repositories,
  refreshMints,
}: UseMintManagerProps) {
  const { toast } = useToast();
  const [activeMintUrl, setActiveMintUrl] = useState<string | null>(null);
  const [mintUrl, setMintUrl] = useState('');
  const [mintToRemove, setMintToRemove] = useState<CashuMint | null>(null);
  const [showRemoveMintModal, setShowRemoveMintModal] = useState(false);

  // Set active mint
  const handleSetActiveMint = useCallback(
    async (url: string | null) => {
      setActiveMintUrl(url);
    },
    []
  );

  // Add a new mint - accepts optional URL parameter or uses mintUrl state
  const handleAddMint = useCallback(async (urlToAdd?: string) => {
    const targetUrl = urlToAdd || mintUrl.trim();
    if (!targetUrl || !coco) return;
    
    try {
      // Normalize URL - add https:// if not present
      let normalizedUrl = targetUrl;
      if (
        !normalizedUrl.startsWith('http://') &&
        !normalizedUrl.startsWith('https://')
      ) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      // Check if mint already exists
      const existingMint = mints.find(mint => 
        mint.url.toLowerCase() === normalizedUrl.toLowerCase()
      );
      
      if (existingMint) {
        throw new Error('This mint is already in your list');
      }

      // Probe reachability from the browser first — a CORS-blocked mint would
      // otherwise be added but fail on every invoice with a cryptic error.
      const probe = await probeMintReachability(normalizedUrl);
      if (probe.reachability === 'cors-blocked') {
        throw new Error(
          `This mint can't be used from a web browser — it doesn't allow web requests (no CORS). It may work in a mobile app, but pick a web-friendly mint here.`,
        );
      }
      if (probe.reachability === 'unreachable') {
        throw new Error(
          `Couldn't reach this mint. Check the URL and that the mint is online, then try again.`,
        );
      }

      // Add mint and automatically trust it
      await coco.mint.addMint(normalizedUrl, { trusted: true });
      
      // Only clear the input if we were using the state URL
      if (!urlToAdd) {
        setMintUrl('');
      }

      if (!urlToAdd) {
        toast({ 
          title: 'Mint added', 
          description: 'Mint added and trusted successfully' 
        });
      }

      await refreshMints();
    } catch (err) {
      if (!urlToAdd) {
        toast({ 
          variant: 'destructive', 
          title: 'Failed to add mint', 
          description: err instanceof Error ? err.message : 'Unknown error' 
        });
      }
      throw err; // Re-throw so external callers can handle it
    }
  }, [mintUrl, coco, mints, toast, refreshMints]);

  // Show remove confirmation modal (for internal use)
  const handleRemoveMintClick = useCallback((mint: CashuMint) => {
    setMintToRemove(mint);
    setShowRemoveMintModal(true);
  }, []);

  // Direct remove mint function (for external components with their own confirmation)
  const handleRemoveMintDirect = useCallback(async (mint: CashuMint) => {
    if (!repositories) return;

    try {
      // Delete the mint from the backend
      await repositories.mintRepository.deleteMint(mint.url);

      // If the removed mint was active, switch to first available
      if (activeMintUrl === mint.url) {
        const remainingMints = mints.filter(
          (m) => m.url !== mint.url
        );
        if (remainingMints.length > 0) {
          handleSetActiveMint(remainingMints[0].url);
        } else {
          setActiveMintUrl(null);
        }
      }

      toast({
        title: 'Mint removed',
        description: `${getCleanMintLabel(mint)} has been removed`
      });

      await refreshMints();
    } catch (err) {
      toast({ 
        variant: 'destructive', 
        title: 'Failed to remove mint', 
        description: err instanceof Error ? err.message : 'Unknown error' 
      });
      throw err;
    }
  }, [
    mints,
    activeMintUrl,
    handleSetActiveMint,
    repositories,
    refreshMints,
    toast,
  ]);

  // Confirm and remove mint (uses internal state)
  const handleConfirmRemoveMint = useCallback(async () => {
    if (!mintToRemove) return;
    await handleRemoveMintDirect(mintToRemove);
    setShowRemoveMintModal(false);
    setMintToRemove(null);
  }, [mintToRemove, handleRemoveMintDirect]);

  return {
    activeMintUrl,
    setActiveMintUrl: handleSetActiveMint,
    mintUrl,
    setMintUrl,
    handleAddMint,
    handleRemoveMintClick,
    handleRemoveMintDirect,
    handleConfirmRemoveMint,
    mintToRemove,
    setMintToRemove,
    showRemoveMintModal,
    setShowRemoveMintModal,
    getCleanMintLabel,
  };
}
